/* eslint-disable sort-imports */
/**
 * Manual control — the small UI pieces both day views share: the drag
 * source / drop target hooks, the custom drag preview, the pin badge with
 * its unpin menu, the confirm dialog a 409 opens, the toast with Undo, and
 * the "fill the gap you just opened" prompt.
 */
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PushPinIcon from "@mui/icons-material/PushPin";
import {
	Alert,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	IconButton,
	Menu,
	MenuItem,
	Snackbar,
	TextField,
	Tooltip,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDrag, useDragLayer, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";

import { MoveGap } from "../../../../../services/roster-assignments-api";
import { destinationOf, ManualMoves } from "../controller/use-manual-moves";
import { DropState } from "../utils/drop-state";
import { canLend, clockOf, defaultLendStart, isoOf, lendLengths, lendProblem, lengthLabel, minutesFrom } from "../utils/lend-window";
import { fmtDay } from "../utils/pin-range";
import { eyebrowSx, lbb } from "../utils/tokens";
import { MoveTarget } from "../utils/undo";

export const CARD_TYPE = "roster-assignment-card";
/** A work-center chip from the week view's header strip. */
export const WORKCENTER_TYPE = "roster-workcenter-chip";
/** A worker's name chip from the week · by line worker list. */
export const WORKER_TYPE = "roster-worker-chip";

export interface WorkcenterDragItem {
	workstation: string
}

export interface WorkerDragItem {
	workerId: string
	workerName: string
}

export interface CardDragItem {
	id: number
	label: string
	workerId: string
	workerName: string
	/** Null when the card's row names no order. */
	workOrderId: number | null
	phaseId: number | null
	/** yyyy-MM-dd of the day cell the card sits in (week · by line); a drop on another day is refused. */
	dayIso?: string
}

const isSelf = (item: CardDragItem, target: MoveTarget): boolean =>
	target.kind === "worker"
		? String(item.workerId) === String(target.workerId)
		: item.workOrderId === target.workOrderId && item.phaseId === target.phaseId;

/** Make an assignment card draggable. The preview is drawn by MoveDragLayer. */
export function useCardDrag(item: CardDragItem, moves: Pick<ManualMoves, "beginDrag" | "endDrag">, enabled = true) {
	const { beginDrag, endDrag } = moves;
	const [{ isDragging }, drag, preview] = useDrag(
		() => ({
			type: CARD_TYPE,
			item: () => {
				beginDrag({ assignmentId: item.id, label: item.label, dayIso: item.dayIso });
				return item;
			},
			canDrag: enabled,
			end: () => endDrag(),
			collect: (monitor) => ({ isDragging: monitor.isDragging() }),
		}),
		[item, enabled, beginDrag, endDrag]
	);
	useEffect(() => {
		preview(getEmptyImage(), { captureDraggingState: true });
	}, [preview]);
	const dragRef = useCallback(
		(node: HTMLElement | null) => {
			drag(node);
		},
		[drag]
	);
	return { isDragging, dragRef };
}

/**
 * Make an element accept a card. The grade decides whether and how.
 * `wholeStep` forces the move to carry every segment the worker holds on
 * the step (the week view's default); left undefined, Shift decides.
 */
export function useCardDrop(
	target: MoveTarget,
	state: DropState,
	moves: Pick<ManualMoves, "requestMove">,
	wholeStep?: boolean,
	/** When given, a name from the people list may be dropped too: one manual block for this step and shift. */
	onPlace?: (worker: WorkerDragItem) => void
) {
	const { requestMove } = moves;
	const [{ isOver, canDrop, chipOver }, drop] = useDrop(
		() => ({
			accept: onPlace ? [CARD_TYPE, WORKER_TYPE] : CARD_TYPE,
			canDrop: (item: CardDragItem | WorkerDragItem, monitor) =>
				monitor.getItemType() === WORKER_TYPE ? Boolean(onPlace) : state.allowed && !isSelf(item as CardDragItem, target),
			drop: (item: CardDragItem | WorkerDragItem, monitor) => {
				if (monitor.getItemType() === WORKER_TYPE) {
					onPlace?.(item as WorkerDragItem);
					return;
				}
				const card = item as CardDragItem;
				requestMove(card.id, target, {
					wholeStep,
					confirmFirst: state.confirm
						? `${card.workerName} — ${state.caption || "not qualified"} for this. Place anyway?`
						: null,
				});
			},
			collect: (monitor) => ({
				isOver: monitor.isOver(),
				canDrop: monitor.canDrop(),
				chipOver: monitor.isOver() && monitor.getItemType() === WORKER_TYPE,
			}),
		}),
		[target, state, requestMove, wholeStep, onPlace]
	);
	const dropRef = useCallback(
		(node: HTMLElement | null) => {
			drop(node);
		},
		[drop]
	);
	return { isOver, canDrop, chipOver, dropRef };
}

/** Make a work-center chip draggable (week view header strip). */
export function useWorkcenterDrag(workstation: string) {
	const [{ isDragging }, drag] = useDrag(
		() => ({
			type: WORKCENTER_TYPE,
			item: { workstation } as WorkcenterDragItem,
			collect: (monitor) => ({ isDragging: monitor.isDragging() }),
		}),
		[workstation]
	);
	const dragRef = useCallback(
		(node: HTMLElement | null) => {
			drag(node);
		},
		[drag]
	);
	return { isDragging, dragRef };
}

/** Make a worker's name chip draggable (week · by line worker list). */
export function useWorkerChipDrag(workerId: string, workerName: string) {
	const [{ isDragging }, drag] = useDrag(
		() => ({
			type: WORKER_TYPE,
			item: { workerId, workerName } as WorkerDragItem,
			collect: (monitor) => ({ isDragging: monitor.isDragging() }),
		}),
		[workerId, workerName]
	);
	const dragRef = useCallback(
		(node: HTMLElement | null) => {
			drag(node);
		},
		[drag]
	);
	return { isDragging, dragRef };
}

/**
 * A lane's row header in the week · by line view takes a worker chip: a
 * standing pin for that worker on the lane for the visible week. Graded
 * neutral — the server decides when Allocate runs.
 */
export function useLaneHeaderDrop(workstation: string, moves: Pick<ManualMoves, "pinToLine">) {
	const { pinToLine } = moves;
	const [{ isOver, dragging }, drop] = useDrop(
		() => ({
			accept: WORKER_TYPE,
			drop: (item: WorkerDragItem) =>
				pinToLine({ workerId: item.workerId, workerName: item.workerName, workstation }),
			collect: (monitor) => ({
				isOver: monitor.isOver(),
				dragging: monitor.getItemType() === WORKER_TYPE,
			}),
		}),
		[workstation, pinToLine]
	);
	const dropRef = useCallback(
		(node: HTMLElement | null) => {
			drop(node);
		},
		[drop]
	);
	return { isOver, dragging, dropRef };
}

/**
 * A worker row in the week view takes two things: a card (= the day move,
 * through the same request as the day views) and a work-center chip (= a
 * standing pin for the week).
 */
export function useWorkerRowDrop(
	target: Extract<MoveTarget, { kind: "worker" }>,
	state: DropState,
	moves: Pick<ManualMoves, "requestMove" | "pinToLine">
) {
	const { requestMove, pinToLine } = moves;
	const [{ isOver, canDrop, chipOver }, drop] = useDrop(
		() => ({
			accept: [CARD_TYPE, WORKCENTER_TYPE],
			canDrop: (item: CardDragItem | WorkcenterDragItem, monitor) =>
				monitor.getItemType() === WORKCENTER_TYPE || (state.allowed && !isSelf(item as CardDragItem, target)),
			drop: (item: CardDragItem | WorkcenterDragItem, monitor) => {
				if (monitor.getItemType() === WORKCENTER_TYPE) {
					pinToLine({
						workerId: target.workerId,
						workerName: target.workerName,
						workstation: (item as WorkcenterDragItem).workstation,
					});
					return;
				}
				const card = item as CardDragItem;
				requestMove(card.id, target, {
					confirmFirst: state.confirm
						? `${card.workerName} — ${state.caption || "not qualified"} for this. Place anyway?`
						: null,
				});
			},
			collect: (monitor) => ({
				isOver: monitor.isOver(),
				canDrop: monitor.canDrop(),
				chipOver: monitor.isOver() && monitor.getItemType() === WORKCENTER_TYPE,
			}),
		}),
		[target, state, requestMove, pinToLine]
	);
	const dropRef = useCallback(
		(node: HTMLElement | null) => {
			drop(node);
		},
		[drop]
	);
	return { isOver, canDrop, chipOver, dropRef };
}

/** The outline and wash a target wears while a card is in the air. */
export const dropSurface = (
	state: DropState,
	dragging: boolean,
	isOver: boolean,
	canDrop: boolean
): React.CSSProperties => {
	if (!dragging) {
		return {};
	}
	return {
		outline: `2px ${state.allowed ? "solid" : "dashed"} ${state.outline}`,
		outlineOffset: -2,
		background: isOver && canDrop ? state.fill : "transparent",
		cursor: state.allowed ? "copy" : "not-allowed",
	};
};

// ---------------------------------------------------------------------------

/** Custom drag preview: card label, plus "whole step" while Shift is held. */
export const MoveDragLayer: React.FC<{ shiftHeld: boolean }> = ({ shiftHeld }) => {
	const { item, isDragging, offset } = useDragLayer((monitor) => ({
		item: monitor.getItem() as CardDragItem | null,
		isDragging: monitor.isDragging() && monitor.getItemType() === CARD_TYPE,
		offset: monitor.getClientOffset(),
	}));
	if (!isDragging || !item || !offset) {
		return null;
	}
	return (
		<div style={{ position: "fixed", pointerEvents: "none", zIndex: 10000, left: 0, top: 0 }}>
			<div
				style={{
					transform: `translate(${offset.x + 12}px, ${offset.y - 14}px)`,
					display: "inline-flex",
					alignItems: "center",
					gap: 8,
					padding: "6px 10px",
					background: lbb.navy,
					color: lbb.paper,
					border: lbb.borderInk,
					borderRadius: 3,
					fontFamily: lbb.fontUi,
					fontSize: 12,
					fontWeight: 600,
					whiteSpace: "nowrap",
					boxShadow: "0 6px 18px rgba(18,24,38,0.25)",
				}}
			>
				<span>{item.workerName}</span>
				<span style={{ color: lbb.navy20, fontWeight: 400 }}>{item.label}</span>
				{shiftHeld && (
					<span
						style={{
							...eyebrowSx,
							fontSize: 10,
							padding: "2px 6px",
							background: lbb.brick,
							color: lbb.paper,
							borderRadius: 2,
						}}
					>
						whole step
					</span>
				)}
			</div>
		</div>
	);
};

// ---------------------------------------------------------------------------

const menuItemSx = { fontFamily: lbb.fontUi, fontSize: 13 };

/**
 * The card's menu. On a pinned card it is the pin icon: Replace…, Remove
 * from step, then the pin options (Keep for the week / Until… / Unpin).
 * On an unpinned card it is a small "…" that only shows on hover or focus
 * (`visible`), with Replace… and Remove. `pinnedUntil` (yyyy-MM-dd) names
 * the standing pin that covers the card, if any. `urgent` is the card of
 * someone who is out: the menu is always visible and "Replace…" leads it,
 * highlighted.
 */
export const CardMenu: React.FC<{
	pinned: boolean
	/** Shown at all when unpinned — the parent card passes its hover / focus state. */
	visible?: boolean
	/** The person is out (or asking to be): Replace… first and highlighted, menu always shown. */
	urgent?: boolean
	onUnpin?: () => void
	onKeepWeek?: () => void
	onKeepUntil?: () => void
	onReplace?: () => void
	onRemove?: () => void
	/** "Move from a time…": send the rest of the block to another step. */
	onSplit?: () => void
	pinnedUntil?: string | null
	size?: number
	color?: string
}> = ({ pinned, visible = true, urgent = false, onUnpin, onKeepWeek, onKeepUntil, onReplace, onRemove, onSplit, pinnedUntil, size = 14, color = lbb.brick }) => {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const open = Boolean(anchor);
	const pick = (action?: () => void) => () => {
		setAnchor(null);
		action?.();
	};
	const title = urgent
		? "This person is out — replace them"
		: pinned
		? pinnedUntil
			? `Pinned until ${fmtDay(pinnedUntil)} — Allocate will not change this. Click for options.`
			: "Pinned — Allocate will not change this. Click for options."
		: "Replace or remove this person";
	const hasPinActions = pinned && Boolean(onUnpin || onKeepWeek || onKeepUntil);
	return (
		<>
			<Tooltip title={title}>
				<IconButton
					size="small"
					aria-label={urgent ? "replace this person" : pinned ? "pinned assignment options" : "assignment options"}
					onClick={(event) => {
						event.stopPropagation();
						setAnchor(event.currentTarget);
					}}
					onMouseDown={(event) => event.stopPropagation()}
					sx={{
						p: 0.25,
						color,
						opacity: pinned || urgent || visible || open ? 1 : 0,
						transition: "opacity 120ms",
						"&:focus-visible": { opacity: 1 },
					}}
				>
					{pinned ? <PushPinIcon sx={{ fontSize: size }} /> : <MoreHorizIcon sx={{ fontSize: size + 2 }} />}
				</IconButton>
			</Tooltip>
			<Menu
				anchorEl={anchor}
				open={open}
				onClose={(event: React.SyntheticEvent) => {
					event.stopPropagation?.();
					setAnchor(null);
				}}
				onClick={(event) => event.stopPropagation()}
			>
				{pinned && pinnedUntil && (
					<MenuItem disabled sx={{ ...menuItemSx, fontSize: 12, opacity: "0.8 !important" }}>
						Standing pin until {fmtDay(pinnedUntil)}
					</MenuItem>
				)}
				{onReplace && (
					<MenuItem
						onClick={pick(onReplace)}
						sx={urgent ? { ...menuItemSx, fontWeight: 700, color: lbb.brickDeep, background: lbb.brickTint } : menuItemSx}
					>
						{urgent ? "Replace… (person is out)" : "Replace…"}
					</MenuItem>
				)}
				{onSplit && (
					<MenuItem onClick={pick(onSplit)} sx={menuItemSx}>
						Move from a time…
					</MenuItem>
				)}
				{onRemove && (
					<MenuItem onClick={pick(onRemove)} sx={{ ...menuItemSx, color: lbb.brickDeep }}>
						Remove from step
					</MenuItem>
				)}
				{hasPinActions && (onReplace || onRemove) && <Divider />}
				{pinned && onKeepWeek && (
					<MenuItem onClick={pick(onKeepWeek)} sx={menuItemSx}>
						Keep for the week
					</MenuItem>
				)}
				{pinned && onKeepUntil && (
					<MenuItem onClick={pick(onKeepUntil)} sx={menuItemSx}>
						Until…
					</MenuItem>
				)}
				{pinned && onUnpin && (
					<MenuItem onClick={pick(onUnpin)} sx={menuItemSx}>
						Unpin: let Allocate change this
					</MenuItem>
				)}
			</Menu>
		</>
	);
};

/** The pin badge of a manually placed card — the pinned form of `CardMenu`. */
export const PinBadge: React.FC<{
	onUnpin: () => void
	onKeepWeek?: () => void
	onKeepUntil?: () => void
	onReplace?: () => void
	onRemove?: () => void
	pinnedUntil?: string | null
	size?: number
	color?: string
}> = (props) => <CardMenu pinned {...props} />;

/**
 * Hover / focus tracking for a card so its "…" menu shows only when the
 * pointer or focus is on it.
 */
export function useCardHover() {
	const [hover, setHover] = useState(false);
	const handlers = useMemo(
		() => ({
			onMouseEnter: () => setHover(true),
			onMouseLeave: () => setHover(false),
			onFocus: () => setHover(true),
			onBlur: (event: React.FocusEvent) => {
				if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
					setHover(false);
				}
			},
		}),
		[]
	);
	return { hover, handlers };
}

// ---------------------------------------------------------------------------

export const ConfirmMoveDialog: React.FC<{ moves: ManualMoves }> = ({ moves }) => {
	const { confirm, acceptConfirm, cancelConfirm, busy } = moves;
	const [wholeStep, setWholeStep] = useState(false);
	useEffect(() => {
		setWholeStep(Boolean(confirm?.request.wholeStep));
	}, [confirm]);
	return (
		<Dialog open={Boolean(confirm)} onClose={cancelConfirm} maxWidth="xs" fullWidth>
			<DialogTitle sx={{ fontFamily: lbb.fontBrand, color: lbb.navy }}>
				{confirm?.flag === "allowOvertime" ? "Place as overtime?" : "Place anyway?"}
			</DialogTitle>
			<DialogContent sx={{ fontFamily: lbb.fontUi, fontSize: 14, color: lbb.ink }}>
				<blockquote
					style={{
						margin: "0 0 12px",
						padding: "8px 12px",
						borderLeft: `3px solid ${lbb.warning}`,
						background: lbb.alloyWarm,
					}}
				>
					{confirm?.message}
				</blockquote>
				<FormControlLabel
					control={<Checkbox size="small" checked={wholeStep} onChange={(e) => setWholeStep(e.target.checked)} />}
					label="Move the whole step (every segment this worker holds on it)"
					sx={{ "& .MuiFormControlLabel-label": { fontFamily: lbb.fontUi, fontSize: 13 } }}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={cancelConfirm} disabled={busy} autoFocus sx={{ color: lbb.ink60 }}>
					Cancel
				</Button>
				<Button
					onClick={() => acceptConfirm(wholeStep)}
					disabled={busy}
					variant="contained"
					sx={{ bgcolor: lbb.brick, "&:hover": { bgcolor: lbb.brickDeep } }}
				>
					{confirm?.flag === "allowOvertime" ? "Place as overtime" : "Place anyway"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

/** "Until…": keep the block's worker on its line through a chosen date. */
export const UntilDialog: React.FC<{ moves: ManualMoves }> = ({ moves }) => {
	const { untilPrompt, cancelUntil, confirmUntil, busy } = moves;
	const [date, setDate] = useState("");
	useEffect(() => {
		setDate(untilPrompt?.defaultDate ?? "");
	}, [untilPrompt]);
	const valid = Boolean(date) && (!untilPrompt || date >= untilPrompt.minDate);
	const handleDateChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => setDate(event.target.value),
		[]
	);
	const handleConfirm = useCallback(() => confirmUntil(date), [confirmUntil, date]);
	return (
		<Dialog open={Boolean(untilPrompt)} onClose={cancelUntil} maxWidth="xs" fullWidth>
			<DialogTitle sx={{ fontFamily: lbb.fontBrand, color: lbb.navy }}>Keep until…</DialogTitle>
			<DialogContent sx={{ fontFamily: lbb.fontUi, fontSize: 14, color: lbb.ink }}>
				<p style={{ margin: "0 0 12px" }}>
					{untilPrompt?.workerName} stays on {untilPrompt?.workstation || "this line"} through the date you pick.
					Days already allocated are re-pinned here and their blocks elsewhere are freed.
				</p>
				<TextField
					size="small"
					fullWidth
					type="date"
					label="Through"
					value={date}
					onChange={handleDateChange}
					autoFocus
					InputLabelProps={{ shrink: true }}
					inputProps={{ min: untilPrompt?.minDate }}
					sx={{ "& .MuiInputBase-input": { fontFamily: lbb.fontUi, fontSize: 14 } }}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={cancelUntil} disabled={busy} sx={{ color: lbb.ink60 }}>
					Cancel
				</Button>
				<Button
					onClick={() => confirmUntil(date)}
					disabled={busy || !valid}
					variant="contained"
					sx={{ bgcolor: lbb.navy, "&:hover": { bgcolor: lbb.ink } }}
				>
					{date ? `Keep through ${fmtDay(date)}` : "Keep"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

/** "Remove from step?" — the slot opens up and Allocate may refill it. */
/**
 * "Remove from step?": the whole block, or only for a while — the person is
 * taken off for those hours and stays on the step before and after.
 */
export const RemoveDialog: React.FC<{ moves: ManualMoves }> = ({ moves }) => {
	const { removePrompt, cancelRemove, confirmRemove, busy } = moves;
	const startIso = removePrompt?.startTime ?? "";
	const endIso = removePrompt?.endTime ?? "";
	const canPart = Boolean(startIso && endIso && canLend(startIso, endIso));
	const [mode, setMode] = useState<"whole" | "while" | "week">("whole");
	const [from, setFrom] = useState(0);
	const [length, setLength] = useState(120);
	const key = removePrompt ? `${removePrompt.assignmentId}` : "";
	useEffect(() => {
		if (!key || !startIso) return;
		setMode("whole");
		setFrom(defaultLendStart(startIso, endIso, null));
		setLength(120);
		// Reset once per prompt.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key]);
	const lengths = useMemo(() => (canPart ? lendLengths(startIso, endIso, from) : []), [canPart, startIso, endIso, from]);
	const until = from + length;
	const problem = canPart && mode === "while" ? lendProblem(startIso, endIso, from, length) : null;
	const handleFrom = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (!event.target.value || !startIso) return;
			const [h, m] = event.target.value.split(":").map(Number);
			let minutes = h * 60 + m;
			if (minutes < minutesFrom(startIso, startIso) - 60) minutes += 24 * 60;
			setFrom(minutes);
			setMode("while");
		},
		[startIso]
	);
	const handleLength = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
		setLength(Number(event.target.value));
		setMode("while");
	}, []);
	const handleWhole = useCallback(() => setMode("whole"), []);
	const handleWhile = useCallback(() => setMode("while"), []);
	const handleWeek = useCallback(() => setMode("week"), []);
	const stopClick = useCallback((event: React.MouseEvent) => event.stopPropagation(), []);
	const handleConfirm = useCallback(() => {
		if (mode === "week") void confirmRemove("week");
		else if (mode === "while" && canPart && !problem) void confirmRemove({ from: isoOf(startIso, from), until: isoOf(startIso, until) });
		else void confirmRemove();
	}, [mode, canPart, problem, confirmRemove, startIso, from, until]);
	const labelSx: React.CSSProperties = { fontFamily: lbb.fontMono, fontSize: 12, color: lbb.ink60 };
	const fieldSx: React.CSSProperties = { font: "inherit", fontFamily: lbb.fontMono, padding: "4px 6px", border: lbb.borderInk, borderRadius: 4, background: lbb.paper };
	const optionSx = (on: boolean): React.CSSProperties => ({
		display: "flex",
		flexDirection: "column",
		gap: 8,
		textAlign: "left",
		padding: "10px 12px",
		border: on ? `2px solid ${lbb.brick}` : lbb.borderHair,
		borderRadius: 4,
		background: on ? lbb.brickTint : lbb.paper,
		cursor: "pointer",
		font: "inherit",
		color: lbb.ink,
	});
	const window = startIso ? `${clockOf(minutesFrom(startIso, startIso))}–${clockOf(minutesFrom(startIso, endIso))}` : "";
	return (
		<Dialog open={Boolean(removePrompt)} onClose={cancelRemove} maxWidth="xs" fullWidth>
			<DialogTitle sx={{ fontFamily: lbb.fontBrand, color: lbb.navy }}>
				Remove {removePrompt?.workerName} from {removePrompt?.stepTitle}?
			</DialogTitle>
			<DialogContent sx={{ fontFamily: lbb.fontUi, fontSize: 14, color: lbb.ink, display: "flex", flexDirection: "column", gap: 1.5 }}>
				<p style={{ margin: 0, color: lbb.ink60, fontSize: 13 }}>
					On {removePrompt?.stepTitle} {window}. The hours you take them off open up, and Allocate may refill them.
				</p>
				<button type="button" onClick={handleWhole} style={optionSx(mode === "whole")} aria-pressed={mode === "whole"}>
					<span style={{ fontWeight: 600, color: lbb.navy }}>Whole block</span>
					<span style={labelSx}>{window}</span>
				</button>
				{canPart && (
					<div role="button" tabIndex={0} onClick={handleWhile} onKeyDown={(e) => e.key === "Enter" && handleWhile()} style={optionSx(mode === "while")} aria-pressed={mode === "while"}>
						<span style={{ fontWeight: 600, color: lbb.navy }}>For a while, then back</span>
						<span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} onClick={stopClick}>
							<select value={length} onChange={handleLength} aria-label="For how long" style={fieldSx}>
								{lengths.map((m) => (
									<option key={m} value={m}>
										{lengthLabel(m)}
									</option>
								))}
								{!lengths.includes(length) && <option value={length}>{lengthLabel(length)}</option>}
							</select>
							<span style={labelSx}>from</span>
							<input type="time" step={900} value={clockOf(from)} onChange={handleFrom} aria-label="From" style={fieldSx} />
						</span>
						<span style={labelSx}>
							Off {clockOf(from)}–{clockOf(until)}, back on {removePrompt?.stepTitle} at {clockOf(until)}
						</span>
					</div>
				)}
				{removePrompt?.workstation && (
					<button type="button" onClick={handleWeek} style={optionSx(mode === "week")} aria-pressed={mode === "week"}>
						<span style={{ fontWeight: 600, color: lbb.navy }}>Whole week on {removePrompt.workstation}</span>
						<span style={labelSx}>
							Every block of theirs on this line, Monday to Sunday ({removePrompt.weekBlocks} {removePrompt.weekBlocks === 1 ? "block" : "blocks"})
						</span>
					</button>
				)}
				{problem && mode === "while" && <div style={{ color: lbb.brickDeep, fontSize: 13 }}>{problem}</div>}
			</DialogContent>
			<DialogActions>
				<Button onClick={cancelRemove} disabled={busy} autoFocus sx={{ color: lbb.ink60 }}>
					Cancel
				</Button>
				<Button
					onClick={handleConfirm}
					disabled={busy || (mode === "while" && Boolean(problem))}
					variant="contained"
					sx={{ bgcolor: lbb.brick, "&:hover": { bgcolor: lbb.brickDeep } }}
				>
					{mode === "week" ? "Remove for the week" : mode === "while" && canPart ? `Remove for ${lengthLabel(length)}` : "Remove"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

/** Bench chip dropped on a line while the person already works elsewhere this week. */
export const PinAskDialog: React.FC<{ moves: ManualMoves }> = ({ moves }) => {
	const { pinAsk, cancelPinAsk, confirmPinAsk, busy } = moves;
	const handleConfirm = useCallback(() => confirmPinAsk(), [confirmPinAsk]);
	return (
		<Dialog open={Boolean(pinAsk)} onClose={cancelPinAsk} maxWidth="xs" fullWidth>
			<DialogTitle sx={{ fontFamily: lbb.fontBrand, color: lbb.navy }}>Already placed this week</DialogTitle>
			<DialogContent sx={{ fontFamily: lbb.fontUi, fontSize: 14, color: lbb.ink }}>
				<p style={{ margin: 0 }}>{pinAsk?.message}</p>
			</DialogContent>
			<DialogActions>
				<Button onClick={cancelPinAsk} disabled={busy} autoFocus sx={{ color: lbb.ink60 }}>
					Keep them where they are
				</Button>
				<Button onClick={handleConfirm} disabled={busy} variant="contained" sx={{ bgcolor: lbb.brick, "&:hover": { bgcolor: lbb.brickDeep } }}>
					Draft the move
				</Button>
			</DialogActions>
		</Dialog>
	);
};

/** A dropped name needs a yes before it is placed: already elsewhere, not qualified, or outside their shift. */
export const PlaceAskDialog: React.FC<{ moves: ManualMoves }> = ({ moves }) => {
	const { placeAsk, cancelPlaceAsk, confirmPlaceAsk, busy } = moves;
	const handleConfirm = useCallback(() => void confirmPlaceAsk(), [confirmPlaceAsk]);
	return (
		<Dialog open={Boolean(placeAsk)} onClose={cancelPlaceAsk} maxWidth="xs" fullWidth>
			<DialogTitle sx={{ fontFamily: lbb.fontBrand, color: lbb.navy }}>{placeAsk?.title}</DialogTitle>
			<DialogContent sx={{ fontFamily: lbb.fontUi, fontSize: 14, color: lbb.ink }}>
				<p style={{ margin: 0 }}>{placeAsk?.message}</p>
			</DialogContent>
			<DialogActions>
				<Button onClick={cancelPlaceAsk} disabled={busy} autoFocus sx={{ color: lbb.ink60 }}>
					Cancel
				</Button>
				<Button onClick={handleConfirm} disabled={busy} variant="contained" sx={{ bgcolor: lbb.brick, "&:hover": { bgcolor: lbb.brickDeep } }}>
					{placeAsk?.confirmLabel}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

/** Shortest piece a mid-shift move may leave or create, in minutes (the server enforces the same). */
export const MIN_SPLIT_MINUTES = 120;

const hhmmOf = (iso: string): string => iso.slice(11, 16);
const toMinutes = (hhmm: string): number => {
	const [h, m] = hhmm.split(":").map(Number);
	return h * 60 + m;
};

/**
 * "Move from a time…": pick when the person changes over and which step they
 * go to. The time must leave at least two hours on each side; the steps are
 * the block's move targets on other lines, graded as a drop would be.
 */
export const SplitDialog: React.FC<{ moves: ManualMoves }> = ({ moves }) => {
	const { splitPrompt, cancelSplit, confirmSplit, busy } = moves;
	const assignment = splitPrompt?.assignment;
	const assignmentId = assignment?.id ?? null;
	const startIso = assignment?.startTime ?? "";
	const endIso = assignment?.endTime ?? "";
	const startMin = startIso ? toMinutes(hhmmOf(startIso)) : 0;
	const endMin = endIso ? toMinutes(hhmmOf(endIso)) + (endIso.slice(0, 10) !== startIso.slice(0, 10) ? 24 * 60 : 0) : 0;
	const earliest = startMin + MIN_SPLIT_MINUTES;
	const latest = endMin - MIN_SPLIT_MINUTES;
	const fits = latest >= earliest;
	const [at, setAt] = useState("");
	const [phaseKey, setPhaseKey] = useState("");
	useEffect(() => {
		if (assignmentId === null) return;
		// Default: halfway, on the quarter hour, inside the allowed range.
		const mid = Math.round((startMin + endMin) / 2 / 15) * 15;
		setAt(clockOf(Math.min(Math.max(mid, earliest), Math.max(earliest, latest))));
		setPhaseKey("");
	}, [assignmentId, startMin, endMin, earliest, latest]);
	const phases = useMemo(
		() => (splitPrompt?.targets?.phases ?? []).filter((p) => p.workOrderId !== assignment?.workOrderId && p.state !== "current"),
		[splitPrompt?.targets, assignment?.workOrderId]
	);
	const chosen = phases.find((p) => `${p.workOrderId}:${p.phaseId}` === phaseKey) ?? null;
	const atMin = at ? toMinutes(at) + (toMinutes(at) < startMin ? 24 * 60 : 0) : 0;
	const problem = !fits
		? `This block is ${Math.round((endMin - startMin) / 60)} h; a mid-shift move needs ${(MIN_SPLIT_MINUTES * 2) / 60} hours of room. Move the whole block instead.`
		: !at
			? "Pick a time."
			: atMin < earliest || atMin > latest
				? `Between ${clockOf(earliest)} and ${clockOf(latest)}, so each part is at least ${MIN_SPLIT_MINUTES / 60} hours.`
				: !chosen
					? "Pick where they go."
					: null;
	const atIso = at ? `${startIso.slice(0, 10)}T${clockOf(atMin)}:00` : "";
	const handleAt = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setAt(event.target.value), []);
	const handleConfirm = useCallback(() => {
		if (chosen && !problem) void confirmSplit(atIso, chosen);
	}, [chosen, problem, confirmSplit, atIso]);
	const handleOverride = useCallback(() => {
		const c = splitPrompt?.confirm;
		if (c) void confirmSplit(c.at, c.phase, { [c.flag]: true });
	}, [splitPrompt?.confirm, confirmSplit]);
	const stateLabel = (state: string): string =>
		state === "ok" ? "open" : state === "full" ? "full" : state === "unqualified" ? "not qualified" : state.replace(/_/g, " ");
	return (
		<Dialog open={Boolean(splitPrompt)} onClose={cancelSplit} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ fontFamily: lbb.fontBrand, color: lbb.navy }}>Move {assignment?.workerName} from a time</DialogTitle>
			<DialogContent sx={{ fontFamily: lbb.fontUi, fontSize: 14, color: lbb.ink, display: "flex", flexDirection: "column", gap: 2 }}>
				{assignment && (
					<p style={{ margin: 0, color: lbb.ink60, fontSize: 13 }}>
						Now on {assignment.workstation ?? "a step"} as {assignment.jobRoleName} {hhmmOf(startIso)}–{hhmmOf(endIso)}. They stay there until the
						time you pick, then go to the step you choose. Each part is at least {MIN_SPLIT_MINUTES / 60} hours.
					</p>
				)}
				<label style={{ display: "flex", alignItems: "center", gap: 12 }}>
					<span style={{ fontFamily: lbb.fontMono, fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase", color: lbb.ink60, width: 120 }}>
						Changes over at
					</span>
					<input
						type="time"
						step={900}
						value={at}
						onChange={handleAt}
						disabled={!fits}
						aria-label="Changes over at"
						style={{ font: "inherit", fontFamily: lbb.fontMono, padding: "6px 8px", border: lbb.borderInk, borderRadius: 4 }}
					/>
				</label>
				<div>
					<div style={{ fontFamily: lbb.fontMono, fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase", color: lbb.ink60, marginBottom: 6 }}>
						Then goes to
					</div>
					{!splitPrompt?.targets && <div style={{ color: lbb.ink60 }}>Loading steps…</div>}
					{splitPrompt?.targets && phases.length === 0 && <div style={{ color: lbb.ink60 }}>No other step is running in this window.</div>}
					<div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 260, overflowY: "auto" }}>
						{phases.map((p) => {
							const key = `${p.workOrderId}:${p.phaseId}`;
							const on = key === phaseKey;
							return (
								<button
									key={key}
									type="button"
									onClick={() => setPhaseKey(key)}
									style={{
										display: "grid",
										gridTemplateColumns: "1fr auto auto",
										gap: 12,
										alignItems: "center",
										textAlign: "left",
										padding: "8px 10px",
										border: on ? `2px solid ${lbb.brick}` : lbb.borderHair,
										borderRadius: 4,
										background: on ? lbb.brickTint : lbb.paper,
										cursor: "pointer",
										font: "inherit",
									}}
								>
									<span style={{ fontWeight: 600, color: lbb.navy }}>
										{p.workstation ?? `step #${p.workOrderId}`}
										<span style={{ fontWeight: 400, color: lbb.ink60 }}>{p.jobRole ? ` · ${p.jobRole}` : ""}</span>
									</span>
									<span style={{ fontFamily: lbb.fontMono, fontSize: 12, color: lbb.ink60 }}>
										{p.have != null && p.needed != null ? `${p.have}/${p.needed}` : ""}
									</span>
									<span
										style={{
											fontFamily: lbb.fontMono,
											fontSize: 11,
											textTransform: "uppercase",
											letterSpacing: "0.06em",
											color: p.state === "ok" ? lbb.success : p.state === "full" ? lbb.ink60 : lbb.warning,
										}}
									>
										{stateLabel(p.state)}
									</span>
								</button>
							);
						})}
					</div>
				</div>
				{splitPrompt?.confirm && (
					<blockquote style={{ margin: 0, padding: "8px 12px", borderLeft: `3px solid ${lbb.warning}`, background: lbb.alloyWarm }}>
						{splitPrompt.confirm.message}
					</blockquote>
				)}
				{problem && !splitPrompt?.confirm && <div style={{ color: lbb.brickDeep, fontSize: 13 }}>{problem}</div>}
			</DialogContent>
			<DialogActions>
				<Button onClick={cancelSplit} disabled={busy} sx={{ color: lbb.ink60 }}>
					Cancel
				</Button>
				{splitPrompt?.confirm ? (
					<Button onClick={handleOverride} disabled={busy} variant="contained" sx={{ bgcolor: lbb.warning, "&:hover": { bgcolor: "#A86B14" } }}>
						Place anyway
					</Button>
				) : (
					<Button
						onClick={handleConfirm}
						disabled={busy || Boolean(problem)}
						variant="contained"
						sx={{ bgcolor: lbb.brick, "&:hover": { bgcolor: lbb.brickDeep } }}
					>
						Move from {at || "…"}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export const MoveToast: React.FC<{ moves: ManualMoves }> = ({ moves }) => {
	const { toast, dismissToast, undo, extend, openUntil, busy } = moves;
	const keep = toast?.keep;
	const hasActions = Boolean(toast?.undo || toast?.restore || toast?.restoreAll?.length || toast?.remove || keep);
	return (
		<Snackbar
			open={Boolean(toast)}
			autoHideDuration={hasActions ? 15000 : 6000}
			onClose={(_, reason) => {
				if (reason !== "clickaway") {
					dismissToast();
				}
			}}
			anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
		>
			<Alert
				severity={toast?.severity ?? "info"}
				onClose={dismissToast}
				action={
					hasActions ? (
						<>
							{(toast?.undo || toast?.restore || toast?.restoreAll?.length || toast?.remove) && (
								<Button color="inherit" size="small" onClick={() => void undo()} disabled={busy}>
									Undo
								</Button>
							)}
							{keep && (
								<Button
									color="inherit"
									size="small"
									onClick={() => void extend(keep.assignmentId, "week")}
									disabled={busy}
									sx={{ whiteSpace: "nowrap" }}
								>
									Keep for the week
								</Button>
							)}
							{keep && (
								<Button color="inherit" size="small" onClick={() => openUntil(keep.assignmentId)} disabled={busy}>
									Until…
								</Button>
							)}
							<Button color="inherit" size="small" onClick={dismissToast}>
								Dismiss
							</Button>
						</>
					) : undefined
				}
				sx={{ fontFamily: lbb.fontUi }}
			>
				<div>{toast?.message}</div>
				{toast?.detail && <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{toast.detail}</div>}
			</Alert>
		</Snackbar>
	);
};

// ---------------------------------------------------------------------------

export const gapHeadline = (gap: MoveGap): string => {
	const when = gap.startTime && gap.endTime ? ` ${gap.startTime.slice(11, 16)}–${gap.endTime.slice(11, 16)}` : "";
	return `${gap.workstation} ${gap.jobRole} now ${gap.have} of ${gap.needed}${when}`;
};

/** "75 CFT Granulation now 1 of 2 · Fill from bench" */
export const GapPrompt: React.FC<{
	gap: MoveGap
	title?: string
	onFill: () => void
	onDismiss: () => void
	inline?: boolean
}> = ({ gap, title, onFill, onDismiss, inline }) => (
	<div
		style={{
			display: "flex",
			alignItems: "center",
			gap: 12,
			padding: inline ? "6px 10px" : "10px 24px",
			background: lbb.brickTint,
			borderTop: inline ? "none" : `1px solid ${lbb.brickSoft}`,
			borderBottom: `1px solid ${lbb.brickSoft}`,
			fontFamily: lbb.fontUi,
			fontSize: 13,
			color: lbb.brickDeep,
		}}
	>
		<span style={{ fontWeight: 600 }}>
			{title ? `${title} ` : ""}
			{gapHeadline(gap)}
		</span>
		<span style={{ color: lbb.navy40 }}>·</span>
		<button
			type="button"
			onClick={onFill}
			style={{
				fontFamily: lbb.fontUi,
				fontSize: 13,
				fontWeight: 700,
				color: lbb.brickDeep,
				background: "none",
				border: "none",
				padding: 0,
				cursor: "pointer",
				textDecoration: "underline",
			}}
		>
			Fill from bench
		</button>
		<button
			type="button"
			onClick={onDismiss}
			aria-label="Dismiss"
			style={{
				marginLeft: "auto",
				fontFamily: lbb.fontUi,
				fontSize: 12,
				color: lbb.ink60,
				background: "none",
				border: "none",
				cursor: "pointer",
			}}
		>
			dismiss
		</button>
	</div>
);

const optionSx = (on: boolean): React.CSSProperties => ({
	display: "flex",
	flexDirection: "column",
	gap: 8,
	textAlign: "left",
	padding: "10px 12px",
	border: on ? `2px solid ${lbb.brick}` : lbb.borderHair,
	borderRadius: 4,
	background: on ? lbb.brickTint : lbb.paper,
	cursor: "pointer",
	font: "inherit",
	color: lbb.ink,
});

const fieldSx: React.CSSProperties = {
	font: "inherit",
	fontFamily: lbb.fontMono,
	padding: "4px 6px",
	border: lbb.borderInk,
	borderRadius: 4,
	background: lbb.paper,
};

/**
 * A card dropped on another step: move the whole block, or only for a while
 * (two hours or more) and come back to their own step afterwards.
 */
export const HowDialog: React.FC<{ moves: ManualMoves }> = ({ moves }) => {
	const { howPrompt, cancelHow, chooseWhole, chooseLend, busy } = moves;
	const assignment = howPrompt?.assignment;
	const startIso = assignment?.startTime ?? "";
	const endIso = assignment?.endTime ?? "";
	const promptKey = howPrompt ? `${assignment?.id}:${howPrompt.target.workOrderId}:${howPrompt.target.phaseId}` : "";
	const [mode, setMode] = useState<"whole" | "while">("whole");
	const [from, setFrom] = useState(0);
	const [length, setLength] = useState(120);
	useEffect(() => {
		if (!promptKey) return;
		setMode("whole");
		setFrom(defaultLendStart(startIso, endIso, howPrompt?.phase?.stepStart));
		setLength(120);
		// Reset once per prompt, not on every grade refresh.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [promptKey]);
	const lengths = useMemo(() => (startIso ? lendLengths(startIso, endIso, from) : []), [startIso, endIso, from]);
	const until = from + length;
	const problem = startIso && mode === "while" ? lendProblem(startIso, endIso, from, length) : null;
	const destination = howPrompt ? destinationOf(howPrompt.phase, howPrompt.target) : "the step";
	const home = assignment?.workstation ?? "their step";

	const handleFrom = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (!event.target.value || !startIso) return;
			// A time earlier than the block's start is the next day (a night block).
			const [h, m] = event.target.value.split(":").map(Number);
			const start = minutesFrom(startIso, startIso);
			let minutes = h * 60 + m;
			if (minutes < start - 60) minutes += 24 * 60;
			setFrom(minutes);
			setMode("while");
		},
		[startIso]
	);
	const handleLength = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
		setLength(Number(event.target.value));
		setMode("while");
	}, []);
	const handleWhole = useCallback(() => setMode("whole"), []);
	const handleWhile = useCallback(() => setMode("while"), []);
	const handleConfirm = useCallback(() => {
		if (mode === "whole") {
			chooseWhole();
		} else if (!problem) {
			void chooseLend(isoOf(startIso, from), isoOf(startIso, until));
		}
	}, [mode, problem, chooseWhole, chooseLend, startIso, from, until]);
	const handleOverride = useCallback(() => {
		const c = howPrompt?.confirm;
		if (c) void chooseLend(c.at, c.until, { [c.flag]: true });
	}, [howPrompt?.confirm, chooseLend]);
	const stopClick = useCallback((event: React.MouseEvent) => event.stopPropagation(), []);
	const labelSx: React.CSSProperties = { fontFamily: lbb.fontMono, fontSize: 12, color: lbb.ink60 };

	return (
		<Dialog open={Boolean(howPrompt)} onClose={cancelHow} maxWidth="xs" fullWidth>
			<DialogTitle sx={{ fontFamily: lbb.fontBrand, color: lbb.navy }}>
				Move {assignment?.workerName} to {destination}
			</DialogTitle>
			<DialogContent sx={{ fontFamily: lbb.fontUi, fontSize: 14, color: lbb.ink, display: "flex", flexDirection: "column", gap: 1.5 }}>
				{assignment && (
					<p style={{ margin: 0, color: lbb.ink60, fontSize: 13 }}>
						Now on {home} as {assignment.jobRoleName} {clockOf(minutesFrom(startIso, startIso))}–{clockOf(minutesFrom(startIso, endIso))}.
					</p>
				)}
				{howPrompt?.warning && (
					<blockquote style={{ margin: 0, padding: "6px 10px", borderLeft: `3px solid ${lbb.warning}`, background: lbb.alloyWarm, fontSize: 13 }}>
						{howPrompt.warning}
					</blockquote>
				)}
				<button type="button" onClick={handleWhole} style={optionSx(mode === "whole")} aria-pressed={mode === "whole"}>
					<span style={{ fontWeight: 600, color: lbb.navy }}>Whole block</span>
					<span style={labelSx}>
						{clockOf(minutesFrom(startIso || "2000-01-01T00:00:00", startIso || "2000-01-01T00:00:00"))}–
						{clockOf(minutesFrom(startIso || "2000-01-01T00:00:00", endIso || "2000-01-01T00:00:00"))} on {destination}
					</span>
				</button>
				<div role="button" tabIndex={0} onClick={handleWhile} onKeyDown={(e) => e.key === "Enter" && handleWhile()} style={optionSx(mode === "while")} aria-pressed={mode === "while"}>
					<span style={{ fontWeight: 600, color: lbb.navy }}>For a while, then back</span>
					<span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} onClick={stopClick}>
						<select value={length} onChange={handleLength} aria-label="For how long" style={fieldSx}>
							{lengths.map((m) => (
								<option key={m} value={m}>
									{lengthLabel(m)}
								</option>
							))}
							{!lengths.includes(length) && <option value={length}>{lengthLabel(length)}</option>}
						</select>
						<span style={labelSx}>from</span>
						<input type="time" step={900} value={clockOf(from)} onChange={handleFrom} aria-label="From" style={fieldSx} />
					</span>
					<span style={labelSx}>
						{clockOf(from)}–{clockOf(until)} on {destination}, back on {home} at {clockOf(until)}
					</span>
				</div>
				{howPrompt?.confirm && (
					<blockquote style={{ margin: 0, padding: "6px 10px", borderLeft: `3px solid ${lbb.warning}`, background: lbb.alloyWarm, fontSize: 13 }}>
						{howPrompt.confirm.message}
					</blockquote>
				)}
				{problem && !howPrompt?.confirm && <div style={{ color: lbb.brickDeep, fontSize: 13 }}>{problem}</div>}
			</DialogContent>
			<DialogActions>
				<Button onClick={cancelHow} disabled={busy} sx={{ color: lbb.ink60 }}>
					Cancel
				</Button>
				{howPrompt?.confirm ? (
					<Button onClick={handleOverride} disabled={busy} variant="contained" sx={{ bgcolor: lbb.warning, "&:hover": { bgcolor: "#A86B14" } }}>
						Place anyway
					</Button>
				) : (
					<Button
						onClick={handleConfirm}
						disabled={busy || Boolean(problem)}
						variant="contained"
						autoFocus
						sx={{ bgcolor: lbb.brick, "&:hover": { bgcolor: lbb.brickDeep } }}
					>
						{mode === "whole" ? "Move whole block" : `Move for ${lengthLabel(length)}`}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};
